export const CHEMICAL_ELEMENTS = Object.freeze([
  'C', 'Cr', 'Ni', 'Al', 'B', 'Co', 'Cu', 'Fe', 'Mg', 'Mn', 'Mo', 'N', 'Nb',
  'P', 'Pb', 'S', 'Si', 'Sn', 'Ti', 'V', 'W', 'Zn',
]);

export const CHEMICAL_ELEMENT_NAMES = Object.freeze({
  Al: 'Aluminium', B: 'Boron', C: 'Carbon', Co: 'Cobalt', Cr: 'Chromium', Cu: 'Copper',
  Fe: 'Iron', Mg: 'Magnesium', Mn: 'Manganese', Mo: 'Molybdenum', N: 'Nitrogen',
  Nb: 'Niobium', Ni: 'Nickel', P: 'Phosphorus', Pb: 'Lead', S: 'Sulfur', Si: 'Silicon',
  Sn: 'Tin', Ti: 'Titanium', V: 'Vanadium', W: 'Tungsten', Zn: 'Zinc',
});

export const createFinderForm = () => ({
  mechanicalProperty: { min: '', max: '', scale: 'MPa_Rm' },
  thermalConductivity: { min: '', max: '' },
  elongation: { min: '', max: '' },
  chemistry: [],
});

const hasBound = range => range.min !== '' || range.max !== '';

export const hasFinderCriterion = form => hasBound(form.mechanicalProperty)
  || hasBound(form.thermalConductivity) || hasBound(form.elongation)
  || form.chemistry.some(hasBound);

const validateRange = (range, label, { maximum = Infinity } = {}) => {
  if (!hasBound(range)) return '';
  const min = range.min === '' ? null : Number(range.min);
  const max = range.max === '' ? null : Number(range.max);
  if ((min !== null && (!Number.isFinite(min) || min < 0 || min > maximum))
      || (max !== null && (!Number.isFinite(max) || max < 0 || max > maximum))) {
    return `${label} contains an invalid value.`;
  }
  if (min !== null && max !== null && min > max) return `${label}: From must not exceed To.`;
  return '';
};

export const validateFinderForm = form => {
  if (!hasFinderCriterion(form)) return 'Enter at least one search criterion.';
  for (const [range, label, options] of [
    [form.mechanicalProperty, 'Mechanical strength / hardness'],
    [form.thermalConductivity, 'Thermal conductivity'],
    [form.elongation, 'Elongation'],
    ...form.chemistry.map(row => [row, row.element || 'Chemical element', { maximum: 100 }]),
  ]) {
    const error = validateRange(range, label, options);
    if (error) return error;
  }
  const elements = form.chemistry.map(row => row.element);
  if (new Set(elements).size !== elements.length) return 'Each chemical element can be added only once.';
  if (form.chemistry.some(row => hasBound(row) && !CHEMICAL_ELEMENTS.includes(row.element))) {
    return 'Select a supported chemical element.';
  }
  return '';
};

const numericRange = range => Object.fromEntries(['min', 'max']
  .filter(bound => range[bound] !== '')
  .map(bound => [bound, Number(range[bound])]));

export const buildFinderCriteria = form => {
  const error = validateFinderForm(form);
  if (error) throw new Error(error);
  const criteria = {};
  if (hasBound(form.mechanicalProperty)) {
    criteria.mechanicalProperty = { scale: form.mechanicalProperty.scale, ...numericRange(form.mechanicalProperty) };
  }
  if (hasBound(form.thermalConductivity)) criteria.thermalConductivity = numericRange(form.thermalConductivity);
  if (hasBound(form.elongation)) criteria.elongation = numericRange(form.elongation);
  const chemistry = form.chemistry.filter(hasBound).map(row => ({ element: row.element, ...numericRange(row) }));
  if (chemistry.length) criteria.chemistry = chemistry;
  return criteria;
};
